import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { CoreService } from '../../../services/core.service';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { saveAs } from 'file-saver';
import { StepperGridComponent } from "../stepper-grid/stepper-grid.component";
import { Router } from '@angular/router';
import { LatestPostsSidebarComponent } from "../latest-posts-sidebar/latest-posts-sidebar.component";


@Component({
  selector: 'app-upload-bulk-step',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MatIconModule, MatButtonModule, StepperGridComponent, LatestPostsSidebarComponent],
  templateUrl: './social-ai-upload-bulk.component.html',
  styleUrls: ['./social-ai-upload-bulk.component.scss']
})
export class SocialAiUploadBulkComponent {
  @Input() companyID = 0;
  @Input() formData: any = {};
  @Output() submitted = new EventEmitter<any>();

  bulkForm: FormGroup;
  selectedFile: File | null = null;
  fileIcon = 'description';

  constructor(
    private readonly fb: FormBuilder,
    private readonly coreService: CoreService,
    private readonly router: Router
  ) {
    this.bulkForm = this.fb.group({
      company_id: [null, Validators.required],
      bulk_file: [null, Validators.required]
    });
  }

  ngOnInit(): void {
    this.companyID = this.companyID || this.coreService.user?.companyID || 0;

    this.bulkForm.patchValue({
      company_id: this.companyID,
      ...this.formData
    });
  }

  downloadCsvTemplate(): void {
    const headers = [
      'Site Url',
      'Site Category',
      'Social Platform',
      'Post Type',
      'Post Status',
      'Scheduled DateTime',
      'Article Url',
      'Caption Title',
      'Description Text',
      'Images'
    ];

    const blob = new Blob([headers.join(';') + '\n'], {
      type: 'text/csv;charset=utf-8'
    });

    saveAs(blob, 'bulk_template.csv');
  }

  downloadXlsxTemplate(): void {
    this.coreService.getData('/bulks/user-download-xlsx', {}, 'blob').subscribe({
      next: (blob: Blob) => saveAs(blob, 'bulk_upload_template.xlsx'),
      error: () => this.coreService.showSnackbar('Failed to download XLSX template.')
    });
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;

    this.selectedFile = file;
    this.bulkForm.patchValue({ bulk_file: file });
    this.updateFileIcon(file.name);
    // this.coreService.showSnackbar(`File "${file.name}" selected and ready to upload.`);
  }

  addFiles(files: FileList): void {
    const file = files[0];
    if (!file) return;

    this.selectedFile = file;
    this.bulkForm.patchValue({ bulk_file: file });
    this.updateFileIcon(file.name);
    // this.coreService.showSnackbar(`File "${file.name}" selected and ready to upload.`);
  }

  private updateFileIcon(fileName: string): void {
    const ext = fileName.split('.').pop()?.toLowerCase();

    switch (ext) {
      case 'csv':
      case 'xls':
      case 'xlsx':
        this.fileIcon = 'table_chart';
        break;
      case 'txt':
        this.fileIcon = 'article';
        break;
      default:
        this.fileIcon = 'description';
    }
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
  }

  onFilesDropped(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();

    if (event.dataTransfer?.files) {
      this.addFiles(event.dataTransfer.files);
    }
  }

  clearFile(): void {
    this.selectedFile = null;
    this.fileIcon = 'description';
    this.bulkForm.patchValue({ bulk_file: null });
  }

  submit(): void {
    if (!this.selectedFile) {
      this.coreService.showSnackbar('Please select a file before submitting.');
      return;
    }

    const formData = new FormData();
    formData.append('company_id', this.companyID.toString());
    formData.append('bulk_file', this.selectedFile);

    this.coreService.postData('bulks/store', formData).subscribe({
      next: (res: any) => {
        if (res.success) {
          this.coreService.showSnackbar('File uploaded successfully!');
          this.submitted.emit(res);
          this.router.navigateByUrl('/upload-bulk/social-ai-bulk-results');
        } else {
          this.coreService.showSnackbar('Upload failed. Please try again.');
        }
      },
      error: () => {
        this.coreService.showSnackbar('File upload failed. Please try again.');
      }
    });
  }
}
